@if (isRequestHeaderUiEnhancementEnabled()) {
  <header class="flex flex-col gap-x-sm">
    <!-- Row 1: back arrow | title | status badge | collaborators | action icons -->
    <div class="flex items-center gap-md flex-wrap">
      <button type="button" (click)="goBack()" class="shrink-0 rtl:rotate-180">
        <lib-chevron-down-icon
          [iconClass]="'size-10 text-neutral-900 rotate-90 p-x-sm rounded-lg border border-neutral-200'" />
      </button>

      <h1 class="text-lg font-semibold text-neutral-900 shrink-0">
        {{ requestDetailsResponse().data.name }}
      </h1>

      @if (copyLinkCondition()) {
        <lib-copy-link
          [clickableLinkTitle]="copyLinkTitle()"
          [linkDescription]="requestDetailsResponse().data.request_subject"
          [urlNavigation]="generateUrl()" />
      }

      @if (showStatus()) {
        <span class="w-px h-lg bg-neutral-300 shrink-0"></span>
        <lib-badge
          [label]="requestDetailsResponse().data.state?.label || ''"
          [badgeClass]="
            'px-md py-x-sm border-none text-xs font-medium text-white ' +
            (requestDetailsResponse().data.state!.value | stepStatus)
          " />
      }

      <div class="flex-1"></div>

      @if (showCollaboratorsSection()) {
        <lib-collaborator-section
          class="shrink-0"
          [assignedList]="assignedList()"
          [rawCollaborators]="rawCollaborators()"
          [assignedListItems]="assignedListTotal()"
          [assignedListTotalPages]="assignedListTotalPages()"
          [isAssignedListLoading]="isAssignedListLoading()"
          [lookupList]="lookupList()"
          [isLookupLoading]="isLookupLoading()"
          [lookupListCurrentPage]="currentPage()"
          [lookupListTotalPages]="totalLookupPages()"
          [lookupListItems]="lookupListTotalItems()"
          [isAddingCollaborator]="isAddCollaboratorLoading()"
          (openAssignCollaboratorsDialog)="openAssignCollaboratorsDialog()"
          (updateAssignedList)="getAssignedCardList.emit($event)"
          (updateLookupList)="onUpdateLookupList($event)"
          (addCollaborator)="onAddCollaboratorInline($event)"
          (deleteCollaborator)="onDeleteCollaborator($event)" />
      }

      @if (showCollaboratorsSection() && (actionsTemplate() || showTrigger())) {
        <span class="w-px h-2xl bg-neutral-300 shrink-0 mx-sm"></span>
      }

      @if (showTrigger()) {
        <div
          libPermissions
          [featurePermissions]="[appPermissions.workflow.trigger]"
          [userPermissions]="requestDetailsResponse().data.permissions"
          class="relative">
          <ng-container [ngTemplateOutlet]="triggerMenu" />
        </div>
      }

      @if (actionsTemplate()) {
        <ng-container *ngTemplateOutlet="actionsTemplate()" />
      }
    </div>

    <!-- Row 2: # ID & Category -->
    @if (
      showRequestId() ||
      (showCategory() && requestType() !== requestTypeEnum.TRIGGER)
    ) {
      <div class="flex items-center gap-lg ps-[3.25rem] flex-wrap mb-md">
        @if (showRequestId()) {
          <p
            class="text-sm font-medium text-neutral-500 flex items-center gap-x-sm">
            <span class="text-neutral-400">#</span>
            {{ requestDetailsResponse().data.request_id }}
          </p>
        }

        @if (showCategory() && requestType() !== requestTypeEnum.TRIGGER) {
          <div class="flex items-center gap-x-sm">
            <lib-tag-right-icon [iconClass]="'size-4 text-neutral-500'" />
            <span class="text-xs text-neutral-700">{{
              requestDetailsResponse().data.category.name!
            }}</span>
          </div>
        }
      </div>
    }

    @if (showDescription()) {
      <div class="ps-[3.25rem] mt-sm pb-md">
        <p class="description text-sm font-medium text-neutral-500">
          {{ requestDetailsResponse().data.description }}
        </p>
      </div>
      <div class="border-b border-neutral-200 mb-md"></div>
    }
  </header>
} @else {
  <header class="flex flex-wrap items-center justify-between mb-xl">
    <div
      class="flex gap-md"
      [class]="showRequestId() ? 'items-start' : 'items-center'">
      <a (click)="goBack()">
        <lib-chevron-down-icon
          [iconClass]="'size-10 text-neutral-900 rotate-90 p-sm rounded-lg cursor-pointer border border-neutral-200'" />
      </a>

      <div>
        <h1
          class="text-xl font-semibold text-neutral-900"
          [class.mt-x-sm]="showRequestId()">
          {{ requestDetailsResponse().data.name }}
        </h1>

        @if (showRequestId()) {
          <p class="font-medium text-neutral-500 mt-sm text-sm">
            {{ requestDetailsResponse().data.request_id }}
          </p>
        }
      </div>
      @if (copyLinkCondition()) {
        <div>
          <lib-copy-link
            [clickableLinkTitle]="copyLinkTitle()"
            [linkDescription]="requestDetailsResponse().data.request_subject"
            [urlNavigation]="generateUrl()" />
        </div>
      }
    </div>

    <div class="flex items-center gap-md">
      @if (showTrigger()) {
        <div
          libPermissions
          [featurePermissions]="[appPermissions.workflow.trigger]"
          [userPermissions]="requestDetailsResponse().data.permissions"
          class="relative">
          <ng-container [ngTemplateOutlet]="triggerMenu" />
        </div>
      }

      @if (showStatus()) {
        <lib-badge
          [label]="requestDetailsResponse().data.state?.label || ''"
          [badgeClass]="
            'px-md py-x-sm border-none text-xs font-medium text-white ' +
            (requestDetailsResponse().data.state!.value | stepStatus)
          " />
      }
    </div>
  </header>

  <section class="px-6xl pb-lg mb-2xl border-b border-neutral-300">
    @if (showDescription()) {
      <p
        class="description text-sm font-medium text-neutral-500 ps-x-sm"
        [class.my-2xl]="showCollaboratorsSection()"
        [class.mb-2xl]="!showCollaboratorsSection()">
        {{ requestDetailsResponse().data.description }}
      </p>
    }

    @let showCollaborators = showCollaboratorsSection();
    @if (showCategory()) {
      <div
        class="flex flex-wrap items-center gap-2xl"
        [class.mb-2xl]="showCollaborators">
        <lib-badge
          [badgeClass]="'px-md py-x-sm text-sm font-medium text-neutral-700 border-none bg-neutral-50'"
          [label]="requestDetailsResponse().data.category.name!" />
      </div>
    }

    @if (showCollaborators) {
      <lib-collaborator-section
        [assignedList]="assignedList()"
        [rawCollaborators]="rawCollaborators()"
        [assignedListItems]="assignedListTotal()"
        [assignedListTotalPages]="assignedListTotalPages()"
        [isAssignedListLoading]="isAssignedListLoading()"
        [lookupList]="lookupList()"
        [isLookupLoading]="isLookupLoading()"
        [lookupListCurrentPage]="currentPage()"
        [lookupListTotalPages]="totalLookupPages()"
        [lookupListItems]="lookupListTotalItems()"
        [isAddingCollaborator]="isAddCollaboratorLoading()"
        (openAssignCollaboratorsDialog)="openAssignCollaboratorsDialog()"
        (updateAssignedList)="getAssignedCardList.emit($event)"
        (updateLookupList)="onUpdateLookupList($event)"
        (addCollaborator)="onAddCollaboratorInline($event)"
        (deleteCollaborator)="onDeleteCollaborator($event)" />
    }
  </section>
}

<ng-template #assignCollaboratorsDialog>
  <lib-assign-group-users
    [groupUsersCards]="lookupList()"
    [selectedGroupUsersCards]="selectedList()"
    (selectGroupUser)="selectItem($event)"
    (unselectGroupUser)="removeSelectedItem($event)"
    (saveAssignedGroupUsers)="saveAssignedItems()"
    (filterGroupUsersCards)="getUsersLookup($event)"
    (paginateGroupUsersCards)="getUsersLookup('', $event)"
    [isLoading]="isLookupLoading()"
    [isSubmitLoading]="isAddCollaboratorLoading()"
    [currentPage]="currentPage()"
    [totalPages]="totalLookupPages()"
    [itemType]="'User'"
    [assignItemsModalTitle]="
      'request_screen_add_collaborators_modal_title' | transloco
    " />
</ng-template>

<ng-template #deleteCollaboratorDialog>
  <lib-delete-confirmation-popup
    [isDeleteItemLoading]="isDeleteCollaboratorLoading()"
    [deleteItemLabels]="{
      deleteItemPopupTitle: 'remove_collaborator_modal_title' | transloco,
      deleteItemPopupDescription:
        'remove_collaborator_modal_description' | transloco,
      deleteItemPopupYesButton:
        'remove_collaborator_modal_yes_remove_it_button' | transloco,
      deleteItemPopupNoButton:
        'remove_collaborator_modal_no_keep_it_button' | transloco,
    }"
    (deleteItem)="deleteCollaborator()"
    (closeModal)="closeDeleteCollaboratorDialog()" />
</ng-template>

<ng-template #triggerMenu>
  @if (enableTriggerDropdown()) {
    <button
      (click)="toggleTriggerMenu()"
      id="request_details_screen_trigger_button_label"
      type="button"
      class="bg-primary-500 text-white font-medium text-base py-sm px-lg border-none rounded-md cursor-pointer flex items-center gap-sm">
      {{ 'request_details_screen_trigger_button_label' | transloco }}
      <lib-chevron-down-icon [iconClass]="'size-4 text-white'" />
    </button>

    @if (isTriggerMenuOpen()) {
      <div
        class="absolute z-20 top-[calc(100%+0.5rem)] end-0 min-w-[13rem] bg-white rounded-lg shadow-lg p-sm flex flex-col gap-x-sm">
        <button
          type="button"
          class="flex items-center gap-x-sm text-left px-sm py-sm rounded text-sm font-medium hover:bg-neutral-50"
          (click)="triggerRequest(requestDetailsResponse().data.id)">
          <lib-arrow-right-icon [iconClass]="'size-4 text-neutral-900'" />
          <p>
            {{ 'request_details_screen_start_now_action_label' | transloco }}
          </p>
        </button>

        @if (showAddScheduleAction()) {
          <button
            type="button"
            libPermissions
            [featurePermissions]="appPermissions.workflow.schedule.add"
            [userPermissions]="requestDetailsResponse().data.permissions"
            class="flex items-center gap-x-sm text-left px-sm py-sm rounded text-sm font-medium hover:bg-neutral-50"
            (click)="openScheduleWorkflow(requestDetailsResponse().data.id)">
            <lib-timer-icon [iconClass]="'size-4 text-neutral-900'" />
            {{
              'request_details_screen_schedule_workflow_action_label'
                | transloco
            }}
          </button>
        }
      </div>
    }
  } @else {
    <button
      (click)="triggerRequest(requestDetailsResponse().data.id)"
      id="request_details_screen_trigger_button_label"
      type="button"
      class="bg-primary-500 text-white font-medium text-base py-sm px-lg border-none rounded-md cursor-pointer flex items-center gap-sm">
      {{ 'request_details_screen_trigger_button_label' | transloco }}
      <lib-arrow-left-icon [iconClass]="'size-5 text-white rotate-180'" />
    </button>
  }
</ng-template>
